import { Routes } from '@angular/router';
import { ErrorViewComponent } from './features/error-view/error-view.component';
import { YardiePicksPageComponent } from './features/yardie-picks/yardie-picks-page.component';
import { StockListComponent } from './features/yardie-picks/stock-list/stock-list.component';
import { ProfilePageComponent } from './features/profile/profile-page.component';
import { PolicyComponent } from './features/policies/policy/policy.component';
import { GrahamPriceCalculatorComponent } from './features/calculators/graham-price-calculator/graham-price-calculator.component';
import { DcfCalculatorComponent } from './features/calculators/dcf-calculator/dcf-calculator.component';
import { GrowthRateCalculatorComponent } from './features/calculators/growth-rate-calculator/growth-rate-calculator.component';
import { MoneyARunCalculatorComponent } from './features/calculators/money-a-run-calculator/money-a-run-calculator.component';
import { BigUpYieldCalculatorComponent } from './features/calculators/big-up-yield-calculator/big-up-yield-calculator.component';
import { CalculatorTabsComponent } from './features/calculators/calculator-tabs/calculator-tabs.component';
import { SectorsComponent } from './features/sectors/sectors.component';
import { SectorListComponent } from './features/sector-list/sector-list.component';
import { IndustryListComponent } from './features/industry-list/industry-list.component';
import { CongressComponent } from './features/congress/congress.component';
import { WatchlistComponent } from './features/watchlist/watchlist.component';
import { PricingPageComponent } from './features/sign-up/pricing/pricing.component';
import { EmailVerificationComponent } from './features/sign-up/email-verification/email-verification.component';
import { roleGuard } from './core/auth/role.guard';
import { onboardingGuard } from './core/auth/onboarding.guard';
import { CompleteProfileComponent } from './features/complete-profile/complete-profile.component';
import { AcceptTermsComponent } from './features/accept-terms/accept-terms.component';
import { DataExportComponent } from './features/profile/data-export/data-export.component';
import { DeleteAccountComponent } from './features/profile/delete-account/delete-account.component';
import { UpdateSubscriptionComponent } from './features/profile/update-subscription/update-subscription.component';
import { DoNotSellComponent } from './features/do-not-sell/do-not-sell.component';
import { ResolvingComponent } from './features/resolving/resolving.component';

export const routes: Routes = [
  // Default route - redirect to resolving page
  { path: '', redirectTo: '/resolving', pathMatch: 'full' },

  // Resolving page - centralized onboarding resolution (no guard)
  { path: 'resolving', component: ResolvingComponent },

  // Dashboard with lazy loading
  {
    path: 'dashboard',
    canActivate: [onboardingGuard, roleGuard('free')],
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then(
        (m) => m.DashboardComponent
      ),
  },

  // Sectors route
  {
    path: 'sectors',
    canActivate: [onboardingGuard, roleGuard('free')],
    component: SectorsComponent,
  },

  // Sector List route
  {
    path: 'sectors/:sectorName',
    canActivate: [onboardingGuard, roleGuard('free')],
    component: SectorListComponent,
  },

  // Industry List route
  {
    path: 'industries/:industryName',
    canActivate: [onboardingGuard, roleGuard('free')],
    component: IndustryListComponent,
  },

  // Congress route
  {
    path: 'congress',
    canActivate: [onboardingGuard, roleGuard('standard')],
    component: CongressComponent,
  },

  // Watchlist route
  {
    path: 'watchlist',
    canActivate: [onboardingGuard, roleGuard('standard')],
    loadComponent: () =>
      import('./features/watchlist/watchlist.component').then(
        (m) => m.WatchlistComponent
      ),
  },
  {
    path: 'watchlist/:id',
    canActivate: [onboardingGuard, roleGuard('standard')],
    loadComponent: () =>
      import(
        './features/watchlist/watchlist-details/watchlist-details.component'
      ).then((m) => m.WatchlistDetailsComponent),
  },
  {
    path: 'screen',
    canActivate: [onboardingGuard, roleGuard('standard')],
    loadComponent: () =>
      import('./features/screener/screener.component').then(
        (m) => m.ScreenerComponent
      ),
  },

  // Stock Detail with nested routing
  {
    path: 'stocks/:ticker',
    canActivate: [onboardingGuard, roleGuard('free')],
    loadComponent: () =>
      import('./features/stock-detail/stock-detail.component').then(
        (m) => m.StockDetailComponent
      ),
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      {
        path: 'overview',
        canActivate: [roleGuard('free')],
        loadComponent: () =>
          import(
            './features/stock-detail/overview-tab/overview-tab.component'
          ).then((m) => m.OverviewTabComponent),
      },
      {
        path: 'quality-score',
        canActivate: [roleGuard('free')],
        loadComponent: () =>
          import(
            './features/stock-detail/quality-score-tab/quality-score-tab.component'
          ).then((m) => m.QualityScoreTabComponent),
      },
      {
        path: 'fundamentals',
        canActivate: [roleGuard('free')],
        loadComponent: () =>
          import(
            './features/stock-detail/fundamentals/fundamentals.component'
          ).then((m) => m.FundamentalsComponent),
      },
      {
        path: 'chart',
        canActivate: [roleGuard('free')],
        loadComponent: () =>
          import(
            './features/stock-detail/chart/chart-tab/chart-tab.component'
          ).then((m) => m.ChartTabComponent),
      },
      {
        path: 'analysis',
        canActivate: [roleGuard('pro')],
        loadComponent: () =>
          import(
            './features/stock-detail/analysis/analysis-tab/analysis-tab.component'
          ).then((m) => m.AnalysisTabComponent),
      },
      {
        path: 'peers',
        canActivate: [roleGuard('free')],
        loadComponent: () =>
          import(
            './features/stock-detail/peers/peers-tab/peers-tab.component'
          ).then((m) => m.PeersTabComponent),
      },
    ],
  },
  {
    path: 'picks',
    canActivate: [onboardingGuard, roleGuard('standard')],
    component: YardiePicksPageComponent,
    children: [
      { path: '', redirectTo: 'toppatop', pathMatch: 'full' },
      { path: ':collectionName', component: StockListComponent },
    ],
  },
  {
    path: 'profile',
    canActivate: [onboardingGuard, roleGuard('free')],
    component: ProfilePageComponent,
  },
  {
    path: 'profile/data-export',
    canActivate: [onboardingGuard, roleGuard('free')],
    component: DataExportComponent,
  },
  {
    path: 'profile/delete-account',
    canActivate: [onboardingGuard, roleGuard('free')],
    component: DeleteAccountComponent,
  },
  {
    path: 'profile/update-subscription',
    canActivate: [onboardingGuard],
    component: UpdateSubscriptionComponent,
  },
  {
    path: 'complete-profile',
    component: CompleteProfileComponent,
  }, // Complete profile after subscription
  {
    path: 'accept-terms',
    component: AcceptTermsComponent,
  }, // Accept Terms of Service and Privacy Policy

  { path: 'pricing', component: PricingPageComponent }, // Pricing page (subscription selection)
  { path: 'sign-up/verify-email', component: EmailVerificationComponent }, // Email verification page

  // Tools/Calculators Routes - Tabbed Interface
  {
    path: 'tools',
    canActivate: [onboardingGuard, roleGuard('free')],
    component: CalculatorTabsComponent,
    children: [
      { path: '', redirectTo: 'dcf', pathMatch: 'full' },
      { path: 'dcf', component: DcfCalculatorComponent },
      { path: 'graham-price', component: GrahamPriceCalculatorComponent },
      { path: 'growth-rate', component: GrowthRateCalculatorComponent },
      { path: 'money-a-run', component: MoneyARunCalculatorComponent },
      { path: 'big-up-yield', component: BigUpYieldCalculatorComponent },
    ],
  },

  // Legal Pages Routes
  { path: 'terms', component: PolicyComponent, data: { policyKind: 'terms' } },
  { path: 'privacy', component: PolicyComponent, data: { policyKind: 'privacy' } },
  { path: 'disclaimer', component: PolicyComponent, data: { policyKind: 'disclaimer' } },
  { path: 'cookies', component: PolicyComponent, data: { policyKind: 'cookies' } },
  { path: 'refund', component: PolicyComponent, data: { policyKind: 'refund' } },
  { path: 'do-not-sell', component: DoNotSellComponent },

  { path: 'error', component: ErrorViewComponent },
];
